import { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Funnel, MagnifyingGlass, X } from '@phosphor-icons/react';
import { APISource, Dataset, FilterPreset, FilterState } from '@/lib/types';
import { FilterPresets } from '@/components/FilterPresets';
import { toast } from 'sonner';

interface DatasetFiltersProps {
  sources: APISource[];
  onFilterChange: (filteredSources: APISource[]) => void;
  selectedDatasets: Dataset[];
}

export function DatasetFilters({
  sources,
  onFilterChange,
  selectedDatasets,
}: DatasetFiltersProps) {
  const [filters, setFilters] = useState<FilterState>({
    searchTerm: '',
    categories: [],
    dataTypes: [],
    qualityScore: 0,
    authRequired: null,
    sortBy: 'popularity',
    sortOrder: 'desc',
  });

  const [isExpanded, setIsExpanded] = useState(false);

  // Extract all available categories and data types
  const availableCategories = useMemo(() => {
    const categories = new Set(sources.map(source => source.category));

    return Array.from(categories).sort();
  }, [sources]);

  const availableDataTypes = useMemo(() => {
    const types = new Set<string>();

    sources.forEach(source => {
      source.datasets.forEach(dataset => {
        dataset.schema.forEach(field => {
          types.add(field.type);
        });
      });
    });

    return Array.from(types).sort();
  }, [sources]);

  // Apply filters to sources
  const filteredSources = useMemo(() => {
    let filtered = [...sources];

    // Search term filter
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();

      filtered = filtered.filter(
        source =>
          source.name.toLowerCase().includes(searchLower) ||
          source.description.toLowerCase().includes(searchLower) ||
          source.datasets.some(
            dataset =>
              dataset.name.toLowerCase().includes(searchLower) ||
              dataset.description.toLowerCase().includes(searchLower)
          )
      );
    }

    // Category filter
    if (filters.categories.length > 0) {
      filtered = filtered.filter(source =>
        filters.categories.includes(source.category)
      );
    }

    // Data type filter
    if (filters.dataTypes.length > 0) {
      filtered = filtered
        .map(source => ({
          ...source,
          datasets: source.datasets.filter(dataset =>
            dataset.schema.some(field => filters.dataTypes.includes(field.type))
          ),
        }))
        .filter(source => source.datasets.length > 0);
    }

    // Quality score filter
    if (filters.qualityScore > 0) {
      filtered = filtered
        .map(source => ({
          ...source,
          datasets: source.datasets.filter(
            dataset => dataset.qualityScore >= filters.qualityScore
          ),
        }))
        .filter(source => source.datasets.length > 0);
    }

    // Auth required filter
    if (filters.authRequired !== null) {
      filtered = filtered.filter(
        source => source.authRequired === filters.authRequired
      );
    }

    // Sort sources
    filtered.sort((a, b) => {
      let aValue: any, bValue: any;

      switch (filters.sortBy) {
        case 'name':
          aValue = a.name;
          bValue = b.name;
          break;
        case 'datasets':
          aValue = a.datasets.length;
          bValue = b.datasets.length;
          break;
        case 'quality':
          aValue =
            a.datasets.reduce((sum, d) => sum + d.qualityScore, 0) /
            a.datasets.length;
          bValue =
            b.datasets.reduce((sum, d) => sum + d.qualityScore, 0) /
            b.datasets.length;
          break;
        case 'popularity':
        default:
          aValue = a.popularity;
          bValue = b.popularity;
          break;
      }

      if (typeof aValue === 'string') {
        return filters.sortOrder === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return filters.sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
    });

    return filtered;
  }, [sources, filters]);

  // Update filters and trigger callback
  const updateFilters = (newFilters: Partial<FilterState>) => {
    const updatedFilters = { ...filters, ...newFilters };

    setFilters(updatedFilters);
  };

  // Clear all filters
  const clearFilters = () => {
    const defaultFilters: FilterState = {
      searchTerm: '',
      categories: [],
      dataTypes: [],
      qualityScore: 0,
      authRequired: null,
      sortBy: 'popularity',
      sortOrder: 'desc',
    };

    setFilters(defaultFilters);
  };

  // Apply preset filters
  const applyPreset = (preset: FilterPreset) => {
    setFilters(preset.filters);
    toast.success(`Applied "${preset.name}" preset`);
  };

  // Apply filters whenever they change
  useMemo(() => {
    onFilterChange(filteredSources);
  }, [filteredSources, onFilterChange]);

  const hasActiveFilters =
    filters.searchTerm ||
    filters.categories.length > 0 ||
    filters.dataTypes.length > 0 ||
    filters.qualityScore > 0 ||
    filters.authRequired !== null;

  const totalDatasets = filteredSources.reduce(
    (sum, source) => sum + source.datasets.length,
    0
  );

  return (
    <div className='space-y-4'>
      {/* Filter Presets */}
      <FilterPresets
        currentFilters={filters}
        onApplyPreset={applyPreset}
        availableCategories={availableCategories}
        availableDataTypes={availableDataTypes}
      />

      {/* Main Filters Card */}
      <Card>
        <CardHeader>
          <div className='flex items-center justify-between'>
            <CardTitle className='flex items-center gap-2'>
              <Funnel className='w-5 h-5' />
              Dataset Filters
            </CardTitle>
            <div className='flex items-center gap-2'>
              <Badge variant='secondary'>
                {filteredSources.length} sources, {totalDatasets} datasets
              </Badge>
              <Button
                variant='ghost'
                size='sm'
                onClick={() => setIsExpanded(!isExpanded)}
              >
                {isExpanded ? 'Hide Filters' : 'Show Filters'}
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className='space-y-4'>
          {/* Search Bar - Always visible */}
          <div className='relative'>
            <MagnifyingGlass className='absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground' />
            <Input
              placeholder='Search APIs, datasets, or descriptions...'
              value={filters.searchTerm}
              onChange={e => updateFilters({ searchTerm: e.target.value })}
              className='pl-10'
            />
          </div>

          {/* Active Filters Summary */}
          {hasActiveFilters && (
            <div className='flex flex-wrap gap-2 items-center'>
              <span className='text-sm text-muted-foreground'>
                Active filters:
              </span>
              {filters.categories.map(category => (
                <Badge key={category} variant='secondary' className='gap-1'>
                  {category}
                  <X
                    className='w-3 h-3 cursor-pointer'
                    onClick={() =>
                      updateFilters({
                        categories: filters.categories.filter(
                          c => c !== category
                        ),
                      })
                    }
                  />
                </Badge>
              ))}
              {filters.dataTypes.map(type => (
                <Badge key={type} variant='outline' className='gap-1'>
                  {type}
                  <X
                    className='w-3 h-3 cursor-pointer'
                    onClick={() =>
                      updateFilters({
                        dataTypes: filters.dataTypes.filter(t => t !== type),
                      })
                    }
                  />
                </Badge>
              ))}
              {filters.qualityScore > 0 && (
                <Badge variant='secondary' className='gap-1'>
                  Quality ≥{filters.qualityScore}%
                  <X
                    className='w-3 h-3 cursor-pointer'
                    onClick={() => updateFilters({ qualityScore: 0 })}
                  />
                </Badge>
              )}
              {filters.authRequired !== null && (
                <Badge variant='secondary' className='gap-1'>
                  {filters.authRequired ? 'Auth Required' : 'No Auth'}
                  <X
                    className='w-3 h-3 cursor-pointer'
                    onClick={() => updateFilters({ authRequired: null })}
                  />
                </Badge>
              )}
              <Button variant='ghost' size='sm' onClick={clearFilters}>
                Clear All
              </Button>
            </div>
          )}

          {/* Expanded Filters */}
          {isExpanded && (
            <>
              <Separator />
              <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6'>
                {/* Categories */}
                <div className='space-y-3'>
                  <Label className='text-sm font-semibold'>Categories</Label>
                  <div className='space-y-2'>
                    {availableCategories.map(category => {
                      const count = sources.filter(
                        s => s.category === category
                      ).length;

                      return (
                        <div
                          key={category}
                          className='flex items-center space-x-2'
                        >
                          <Checkbox
                            id={`category-${category}`}
                            checked={filters.categories.includes(category)}
                            onCheckedChange={checked => {
                              if (checked) {
                                updateFilters({
                                  categories: [...filters.categories, category],
                                });
                              } else {
                                updateFilters({
                                  categories: filters.categories.filter(
                                    c => c !== category
                                  ),
                                });
                              }
                            }}
                          />
                          <label
                            htmlFor={`category-${category}`}
                            className='text-sm font-normal cursor-pointer flex-1'
                          >
                            {category} ({count})
                          </label>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Data Types */}
                <div className='space-y-3'>
                  <Label className='text-sm font-semibold'>Data Types</Label>
                  <div className='space-y-2'>
                    {availableDataTypes.map(type => {
                      const count = sources.reduce(
                        (sum, source) =>
                          sum +
                          source.datasets.filter(dataset =>
                            dataset.schema.some(field => field.type === type)
                          ).length,
                        0
                      );

                      return (
                        <div key={type} className='flex items-center space-x-2'>
                          <Checkbox
                            id={`type-${type}`}
                            checked={filters.dataTypes.includes(type)}
                            onCheckedChange={checked => {
                              if (checked) {
                                updateFilters({
                                  dataTypes: [...filters.dataTypes, type],
                                });
                              } else {
                                updateFilters({
                                  dataTypes: filters.dataTypes.filter(
                                    t => t !== type
                                  ),
                                });
                              }
                            }}
                          />
                          <label
                            htmlFor={`type-${type}`}
                            className='text-sm font-normal cursor-pointer flex-1'
                          >
                            {type} ({count})
                          </label>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Quality & Auth */}
                <div className='space-y-4'>
                  <div className='space-y-2'>
                    <Label className='text-sm font-semibold'>
                      Minimum Quality Score
                    </Label>
                    <Select
                      value={filters.qualityScore.toString()}
                      onValueChange={value =>
                        updateFilters({ qualityScore: parseInt(value) })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder='Any quality' />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value='0'>Any quality</SelectItem>
                        <SelectItem value='70'>70% or higher</SelectItem>
                        <SelectItem value='80'>80% or higher</SelectItem>
                        <SelectItem value='90'>90% or higher</SelectItem>
                        <SelectItem value='95'>95% or higher</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className='space-y-2'>
                    <Label className='text-sm font-semibold'>
                      Authentication
                    </Label>
                    <Select
                      value={
                        filters.authRequired === null
                          ? 'any'
                          : filters.authRequired.toString()
                      }
                      onValueChange={value =>
                        updateFilters({
                          authRequired:
                            value === 'any' ? null : value === 'true',
                        })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder='Any auth requirement' />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value='any'>Any</SelectItem>
                        <SelectItem value='false'>
                          No authentication required
                        </SelectItem>
                        <SelectItem value='true'>
                          Authentication required
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Sort Options */}
                <div className='space-y-4'>
                  <div className='space-y-2'>
                    <Label className='text-sm font-semibold'>Sort By</Label>
                    <Select
                      value={filters.sortBy}
                      onValueChange={(value: FilterState['sortBy']) =>
                        updateFilters({ sortBy: value })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value='popularity'>Popularity</SelectItem>
                        <SelectItem value='name'>Name</SelectItem>
                        <SelectItem value='datasets'>Dataset Count</SelectItem>
                        <SelectItem value='quality'>Average Quality</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className='space-y-2'>
                    <Label className='text-sm font-semibold'>Sort Order</Label>
                    <Select
                      value={filters.sortOrder}
                      onValueChange={(value: FilterState['sortOrder']) =>
                        updateFilters({ sortOrder: value })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value='desc'>Descending</SelectItem>
                        <SelectItem value='asc'>Ascending</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
